// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { adminAPI, authAPI, groupAPI, contactAPI } from "@/lib/api";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Users, 
  FolderOpen, 
  BookOpen, 
  FileText, 
  Video, 
  Activity,
  Settings,
  Loader2,
  Mail,
  Server,
  Database,
  Save,
  Trash2,
  Shield,
  User,
  GraduationCap,
  UserPlus,
  Plus,
  Pencil,
  UserCheck,
  UserX,
  Clock,
  CalendarClock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Share2,
  CreditCard,
  Landmark,
  GitBranch,
  UsersRound,
  UserPlus as UserPlusIcon,
  UserMinus,
  ChevronDown,
  ChevronRight,
  Search,
  Sparkles,
  Key,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Send,
  MessageSquare,
  FlaskConical,
  Globe,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatRelativeTime, getInitials } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import GitConnectionsConfig from "@/components/admin/GitConnectionsConfig";
import LabsAdminTab from "@/pages/LabsAdminTab";
import BackupAdminTab from "@/components/admin/BackupAdminTab";
import TempSessionsAdminTab from "@/components/admin/TempSessionsAdminTab";
import OverviewTab from "@/components/admin/OverviewTab";
import DocumentationAdminTab from "@/components/admin/DocumentationAdminTab";
import SecurityAdminTab from "@/components/admin/SecurityAdminTab";
import TutorialsListPage from "@/pages/tutorials/TutorialsListPage";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, permissions } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });
  const [autoApprove, setAutoApprove] = useState(false);
  const [monDossierEnabled, setMonDossierEnabled] = useState(true);
  const [lockedUsers, setLockedUsers] = useState([]);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Create user state
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "candidate"
  });

  // Edit user state
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(false);
  const [editUser, setEditUser] = useState(null);

  // Expiration dialog state
  const [expirationDialogOpen, setExpirationDialogOpen] = useState(false);
  const [expirationUser, setExpirationUser] = useState(null);
  const [expirationDate, setExpirationDate] = useState("");
  const [savingExpiration, setSavingExpiration] = useState(false);

  // Config states
  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_email: "",
    use_tls: true,
  });
  const [jitsiConfig, setJitsiConfig] = useState({
    server_url: "",
    app_id: "",
    secret_key: "",
  });
  const [s3Config, setS3Config] = useState({
    endpoint_url: "",
    access_key: "",
    secret_key: "",
    bucket_name: "",
    region: "us-east-1",
  });
  const [sharingConfig, setSharingConfig] = useState({
    sharing_enabled: true,
    share_domain: "",
  });
  const [paymentConfig, setPaymentConfig] = useState({
    stripe_enabled: false,
    stripe_configured: false,
    stripe_publishable_key: "",
    stripe_secret_key: "",
    bank_transfer_enabled: false,
    bank_name: "",
    bank_iban: "",
    bank_bic: "",
    bank_account_holder: "",
    bank_instructions: ""
  });
  const [savingConfig, setSavingConfig] = useState(null);

  // Google Drive Config
  const [gdriveConfig, setGdriveConfig] = useState({
    credentials_json: "",
    root_folder_id: "",
  });
  const [showGdriveKey, setShowGdriveKey] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());

  // Email domains state
  const [emailDomains, setEmailDomains] = useState([]);
  const [emailDomainsDefaults, setEmailDomainsDefaults] = useState([]);
  const [emailDomainsSource, setEmailDomainsSource] = useState("env");
  const [newDomain, setNewDomain] = useState("");
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [savingDomains, setSavingDomains] = useState(false);

  // Groups state
  const [workspaces, setWorkspaces] = useState([]);
  const [adminGroups, setAdminGroups] = useState([]);
  const [adminGroupMembers, setAdminGroupMembers] = useState({});
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [savingGroup, setSavingGroup] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedGroupForMember, setSelectedGroupForMember] = useState(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [addingMember, setAddingMember] = useState(null);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [expandedAdminGroups, setExpandedAdminGroups] = useState({});
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [collapsedCandidateGroups, setCollapsedCandidateGroups] = useState({});

  // LLM Config state
  const [llmConfig, setLlmConfig] = useState({
    provider: 'openai',
    model: 'gpt-5.2',
    api_key: '',
    
  });
  const [hasCustomKey, setHasCustomKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [loadingLlm, setLoadingLlm] = useState(false);
  const [llmWorkspaceId, setLlmWorkspaceId] = useState("");

  const llmProviders = [
    { value: 'openai', label: 'OpenAI', models: [
      { value: 'gpt-5.2', label: 'GPT-5.2 (Recommandé)' },
      { value: 'gpt-5.1', label: 'GPT-5.1' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4.1', label: 'GPT-4.1' }
    ]},
    { value: 'anthropic', label: 'Anthropic (Claude)', models: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommandé)' },
      { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
      { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }
    ]},
    { value: 'gemini', label: 'Google Gemini', models: [
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Recommandé)' },
      { value: 'gemini-3-pro-preview', label: 'Gemini 3 Pro' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' }
    ]}
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Lazy-load data when switching tabs via sidebar
  useEffect(() => {
    if (activeTab === "groups" && adminGroups.length === 0 && !loadingGroups) {
      loadGroups();
    }
    if (activeTab === "llm-config" && workspaces.length === 0) {
      loadWorkspaces();
    }
    if (activeTab === "users" && workspaces.length === 0) {
      loadWorkspaces();
    }
    if (activeTab === "email-templates" && emailTemplates.length === 0 && !loadingTemplates) {
      setLoadingTemplates(true);
      adminAPI.getEmailTemplates()
        .then(res => setEmailTemplates(res.data.templates || []))
        .catch(() => toast.error("Erreur lors du chargement des templates"))
        .finally(() => setLoadingTemplates(false));
    }
    if (activeTab === "config" && emailDomains.length === 0 && !loadingDomains) {
      loadEmailDomains();
    }
  }, [activeTab]);

  const loadEmailDomains = async () => {
    setLoadingDomains(true);
    try {
      const res = await adminAPI.getEmailDomains();
      setEmailDomains(res.data.domains || []);
      setEmailDomainsDefaults(res.data.defaults || []);
      setEmailDomainsSource(res.data.source || "env");
    } catch (error) {
      console.error("Failed to load email domains:", error);
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    // Basic domain validation
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/.test(domain)) {
      toast.error("Format de domaine invalide (ex: gmail.com)");
      return;
    }
    if (emailDomains.includes(domain)) {
      toast.error("Ce domaine existe déjà");
      return;
    }
    setEmailDomains([...emailDomains, domain]);
    setNewDomain("");
  };

  const handleRemoveDomain = (domain) => {
    setEmailDomains(emailDomains.filter(d => d !== domain));
  };

  const handleSaveEmailDomains = async () => {
    if (emailDomains.length === 0) {
      toast.error("Au moins un domaine est requis");
      return;
    }
    setSavingDomains(true);
    try {
      await adminAPI.updateEmailDomains(emailDomains);
      setEmailDomainsSource("database");
      toast.success("Domaines email autorisés mis à jour (appliqué immédiatement)");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingDomains(false);
    }
  };

  const handleResetEmailDomains = async () => {
    setSavingDomains(true);
    try {
      const res = await adminAPI.resetEmailDomains();
      setEmailDomains(res.data.domains || []);
      setEmailDomainsSource("env");
      toast.success("Réinitialisé aux valeurs par défaut (.env)");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setSavingDomains(false);
    }
  };

  const loadData = async () => {
    try {
      // Stats & activities — accessible to trainers and admins
      const [statsRes, activitiesRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getActivities(50),
      ]);
      setStats(statsRes.data);
      setActivities(activitiesRes.data);

      // Admin-only data
      if (isAdmin) {
        const [usersRes, pendingRes, autoApproveRes, lockedRes, monDossierRes] = await Promise.all([
          adminAPI.listUsers(),
          adminAPI.listPendingUsers(),
          adminAPI.getAutoApprove().catch(() => ({ data: { enabled: false } })),
          adminAPI.listLockedUsers().catch(() => ({ data: [] })),
          adminAPI.getMonDossierEnabled().catch(() => ({ data: { enabled: true } })),
        ]);
        setUsers(usersRes.data);
        setPendingUsers(pendingRes.data);
        setAutoApprove(autoApproveRes.data?.enabled || false);
        setLockedUsers(lockedRes.data);
        setMonDossierEnabled(monDossierRes.data?.enabled !== false);

        // Load configs
        const [smtpRes, jitsiRes, s3Res] = await Promise.all([
          adminAPI.getConfig("smtp"),
          adminAPI.getConfig("jitsi"),
          adminAPI.getConfig("s3"),
        ]);
        if (smtpRes.data.value) setSmtpConfig(smtpRes.data.value);
        if (jitsiRes.data.value) setJitsiConfig(jitsiRes.data.value);
        if (s3Res.data.value) setS3Config(s3Res.data.value);

        try {
          const gdriveRes = await adminAPI.getConfig("gdrive");
          if (gdriveRes.data.value) setGdriveConfig(gdriveRes.data.value);
        } catch (e) { console.log("Google Drive config not available"); }

        try {
          const sharingRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sharing/config`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (sharingRes.ok) setSharingConfig(await sharingRes.json());
        } catch (e) { console.log("Sharing config not available"); }

        try {
          const wsId = localStorage.getItem('current_workspace_id');
          if (wsId) {
            const paymentRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/payment-config?workspace_id=${wsId}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (paymentRes.ok) {
              const paymentData = await paymentRes.json();
              setPaymentConfig(prev => ({ ...prev, ...paymentData }));
            }
          }
        } catch (e) { console.log("Payment config not available"); }
      }
    } catch (error) {
      console.error("Failed to load admin data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Load workspaces for LLM config tab
  const loadWorkspaces = async () => {
    try {
      const wsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (wsRes.ok) {
        const wsData = await wsRes.json();
        setWorkspaces(wsData);
      }
    } catch (e) {
      console.error("Failed to load workspaces:", e);
    }
  };

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const groupsRes = await groupAPI.list();
      setAdminGroups(groupsRes.data);
      
      const membersMap = {};
      for (const g of groupsRes.data) {
        if (g.members && g.members.length > 0) {
          try {
            const membersRes = await groupAPI.listMembers(g.id);
            membersMap[g.id] = membersRes.data;
          } catch {
            membersMap[g.id] = [];
          }
        } else {
          membersMap[g.id] = [];
        }
      }
      setAdminGroupMembers(membersMap);
      const expanded = {};
      groupsRes.data.forEach(g => { expanded[g.id] = true; });
      setExpandedAdminGroups(expanded);
    } catch (error) {
      console.error("Failed to load groups:", error);
      toast.error("Erreur lors du chargement des groupes");
    } finally {
      setLoadingGroups(false);
    }
  };

  const openAdminGroupDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setGroupForm({ name: group.name, description: group.description || "" });
    } else {
      setEditingGroup(null);
      setGroupForm({ name: "", description: "" });
    }
    setGroupDialogOpen(true);
  };

  const handleSaveAdminGroup = async () => {
    if (!groupForm.name.trim()) return;
    setSavingGroup(true);
    try {
      if (editingGroup) {
        await groupAPI.update(editingGroup.id, groupForm);
        toast.success("Groupe modifié");
      } else {
        await groupAPI.create(groupForm);
        toast.success("Groupe créé");
      }
      setGroupDialogOpen(false);
      loadGroups();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteAdminGroup = async () => {
    if (!deleteGroupId) return;
    setDeletingGroup(true);
    try {
      await groupAPI.delete(deleteGroupId);
      toast.success("Groupe supprimé");
      loadGroups();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeletingGroup(false);
      setDeleteGroupId(null);
    }
  };

  const handleAddAdminMember = async (userId) => {
    if (!selectedGroupForMember) return;
    setAddingMember(userId);
    try {
      await groupAPI.addMember(selectedGroupForMember, userId);
      toast.success("Membre ajouté");
      loadGroups();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAddingMember(null);
    }
  };

  const handleRemoveAdminMember = async (groupId, userId) => {
    try {
      await groupAPI.removeMember(groupId, userId);
      toast.success("Membre retiré");
      loadGroups();
    } catch (error) {
      toast.error("Erreur lors du retrait");
    }
  };

  // LLM Config handlers
  const loadLlmConfig = async (wsId) => {
    if (!wsId) return;
    setLoadingLlm(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/training/llm-config?workspace_id=${wsId}`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setLlmConfig(prev => ({ ...prev, provider: data.provider, model: data.model }));
        setHasCustomKey(data.has_custom_key);
      }
    } catch (error) {
      console.error('Failed to load LLM config:', error);
    } finally {
      setLoadingLlm(false);
    }
  };

  const handleSaveLlmConfig = async () => {
    if (!llmWorkspaceId) return;
    setSavingLlm(true);
    try {
      const payload = { provider: llmConfig.provider, model: llmConfig.model };
      if (llmConfig.api_key ) payload.api_key = llmConfig.api_key;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/training/llm-config?workspace_id=${llmWorkspaceId}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      if (response.ok) {
        toast.success('Configuration IA sauvegardée');
        loadLlmConfig(llmWorkspaceId);
      } else throw new Error('Save failed');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingLlm(false);
    }
  };

  const handleSelectLlmWorkspace = (wsId) => {
    setLlmWorkspaceId(wsId);
    loadLlmConfig(wsId);
  };

  const getLlmModels = () => {
    const provider = llmProviders.find(p => p.value === llmConfig.provider);
    return provider?.models || [];
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error("Veuillez entrer une adresse e-mail valide.");
      return;
    }

    if (newUser.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setCreatingUser(true);
    try {
      const response = await authAPI.register(newUser);
      setUsers([...users, response.data.user]);
      setCreateUserOpen(false);
      setNewUser({
        email: "",
        password: "",
        first_name: "",
        last_name: "",
        role: "candidate"
      });
      toast.success("Utilisateur créé avec succès");
      loadData(); // Reload stats
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success("Rôle mis à jour");
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    setDeleting(true);

    try {
      await adminAPI.deleteUser(deleteUserId);
      setUsers(users.filter(u => u.id !== deleteUserId));
      setSelectedUsers(prev => { const n = new Set(prev); n.delete(deleteUserId); return n; });
      toast.success("Utilisateur supprimé");
      loadData();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteUserId(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUsers.size === 0) return;
    const toDelete = [...selectedUsers].filter(id => id !== user?.id);
    if (toDelete.length === 0) { toast.error("Vous ne pouvez pas vous supprimer vous-même"); return; }
    if (!window.confirm(`Supprimer ${toDelete.length} utilisateur(s) ?`)) return;
    setDeleting(true);
    let deleted = 0;
    for (const uid of toDelete) {
      try { await adminAPI.deleteUser(uid); deleted++; } catch {}
    }
    toast.success(`${deleted} utilisateur(s) supprimé(s)`);
    setSelectedUsers(new Set());
    setDeleting(false);
    loadData();
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      const n = new Set(prev);
      if (n.has(userId)) n.delete(userId); else n.add(userId);
      return n;
    });
  };

  const toggleSelectAll = (userIds) => {
    setSelectedUsers(prev => {
      const allSelected = userIds.every(id => prev.has(id));
      const n = new Set(prev);
      if (allSelected) { userIds.forEach(id => n.delete(id)); } else { userIds.forEach(id => n.add(id)); }
      return n;
    });
  };

  const handleEditUser = (userToEdit) => {
    setEditUser({
      id: userToEdit.id,
      email: userToEdit.email,
      first_name: userToEdit.first_name,
      last_name: userToEdit.last_name,
      role: userToEdit.role,
      new_password: ""
    });
    setEditUserOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;

    setEditingUser(true);
    try {
      await adminAPI.updateUser(editUser.id, {
        email: editUser.email,
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        role: editUser.role,
        new_password: editUser.new_password || undefined,
        is_approved: editUser.is_approved,
        account_expires_at: editUser.account_expires_at || null
      });
      
      setUsers(users.map(u => u.id === editUser.id ? {
        ...u,
        email: editUser.email,
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        role: editUser.role,
        is_approved: editUser.is_approved,
        account_expires_at: editUser.account_expires_at
      } : u));
      
      // Also update pending users if status changed
      if (editUser.is_approved) {
        setPendingUsers(pendingUsers.filter(u => u.id !== editUser.id));
      }
      
      setEditUserOpen(false);
      setEditUser(null);
      toast.success("Utilisateur mis à jour");
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setEditingUser(false);
    }
  };

  // Approve a pending user
  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      // Reload users to get updated list
      const usersRes = await adminAPI.listUsers();
      setUsers(usersRes.data);
      toast.success("Utilisateur approuvé");
    } catch (error) {
      console.error("Failed to approve user:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    }
  };

  // Unlock a locked user
  const handleUnlockUser = async (userId) => {
    try {
      await adminAPI.unlockUser(userId);
      setLockedUsers(lockedUsers.filter(u => u.id !== userId));
      toast.success("Compte débloqué avec succès");
    } catch (error) {
      console.error("Failed to unlock user:", error);
      toast.error("Erreur lors du déblocage");
    }
  };

  // Reject a pending user
  const handleRejectUser = async (userId, reason = null) => {
    try {
      await adminAPI.rejectUser(userId, reason);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      toast.success("Utilisateur rejeté");
    } catch (error) {
      console.error("Failed to reject user:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du rejet");
    }
  };

  // Set user expiration
  const handleSetExpiration = async () => {
    if (!expirationUser) return;
    setSavingExpiration(true);
    try {
      await adminAPI.setUserExpiration(expirationUser.id, expirationDate || null);
      // Update user in list
      setUsers(users.map(u => u.id === expirationUser.id 
        ? { ...u, account_expires_at: expirationDate || null } 
        : u
      ));
      setExpirationDialogOpen(false);
      setExpirationUser(null);
      setExpirationDate("");
      toast.success(expirationDate ? "Date d'expiration définie" : "Date d'expiration supprimée");
    } catch (error) {
      console.error("Failed to set expiration:", error);
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSavingExpiration(false);
    }
  };

  // Toggle auto approve
  const handleToggleAutoApprove = async (enabled) => {
    try {
      await adminAPI.setAutoApprove(enabled);
      setAutoApprove(enabled);
      toast.success(enabled ? "Approbation automatique activée" : "Approbation automatique désactivée");
    } catch (error) {
      console.error("Failed to toggle auto approve:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  // Toggle Mon Dossier visibility for candidates
  const handleToggleMonDossier = async (enabled) => {
    try {
      await adminAPI.setMonDossierEnabled(enabled);
      setMonDossierEnabled(enabled);
      toast.success(enabled ? "Menu 'Mon dossier' activé pour les candidats" : "Menu 'Mon dossier' désactivé pour les candidats");
    } catch (error) {
      console.error("Failed to toggle Mon Dossier:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  // Open expiration dialog
  const openExpirationDialog = (u) => {
    setExpirationUser(u);
    setExpirationDate(u.account_expires_at ? u.account_expires_at.split('T')[0] : "");
    setExpirationDialogOpen(true);
  };

  // Check if account is expired
  const isAccountExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const handleSaveConfig = async (type) => {
    setSavingConfig(type);

    try {
      switch (type) {
        case "smtp":
          await adminAPI.updateSMTPConfig(smtpConfig);
          break;
        case "jitsi":
          await adminAPI.updateJitsiConfig(jitsiConfig);
          break;
        case "s3":
          await adminAPI.updateS3Config(s3Config);
          break;
        case "gdrive":
          await adminAPI.updateConfig("gdrive", gdriveConfig);
          break;
      }
      toast.success("Configuration enregistrée");
    } catch (error) {
      console.error("Failed to save config:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingConfig(null);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: "default",
      trainer: "secondary",
      candidate: "outline",
    };
    const icons = {
      admin: Shield,
      trainer: GraduationCap,
      candidate: User,
    };
    const labels = {
      admin: "Admin",
      trainer: "Formateur",
      manager: "Gestionnaire",
      candidate: "Candidat",
    };
    const Icon = icons[role];
    return (
      <Badge variant={variants[role]} className="gap-1">
        <Icon className="h-3 w-3" />
        {labels[role]}
      </Badge>
    );
  };

  const getActivityIcon = (action) => {
    const icons = {
      create: "bg-green-500",
      update: "bg-blue-500",
      delete: "bg-red-500",
      login: "bg-purple-500",
      register: "bg-indigo-500",
      submit: "bg-orange-500",
      grade: "bg-yellow-500",
    };
    return icons[action] || "bg-gray-500";
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }



  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Administration</h1>
            <p className="mt-1 text-muted-foreground">
              Gérez votre plateforme e-learning
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* TabsList hidden — navigation is via the sidebar tree */}

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <OverviewTab stats={stats} activities={activities} loading={loading} />
          </TabsContent>

          {/* Locked Accounts Tab */}
          <TabsContent value="locked-accounts" className="space-y-6">
            <Card className="border-red-100 dark:border-red-900 overflow-hidden shadow-sm">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
              <CardHeader className="bg-red-50/50 dark:bg-red-900/10 pb-4">
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <Lock className="h-5 w-5" />
                  Comptes temporairement bloqués
                </CardTitle>
                <CardDescription className="text-red-600/70 dark:text-red-400/70">
                  {lockedUsers.length} utilisateur(s) ayant dépassé le nombre de tentatives de connexion
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {lockedUsers.length === 0 ? (
                  <div className="text-center py-12 flex flex-col items-center justify-center animate-in fade-in duration-500">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle className="h-8 w-8 text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Aucun compte bloqué</h3>
                    <p className="text-sm text-gray-500">Tous les utilisateurs ont accès à leurs comptes.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {lockedUsers.map((u, index) => (
                      <div 
                        key={u.id} 
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-red-100 rounded-xl bg-white hover:bg-red-50/30 transition-colors gap-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <Avatar className="h-12 w-12 border-2 border-red-100">
                              <AvatarFallback className="bg-red-50 text-red-700 font-semibold">
                                {getInitials(u.first_name, u.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-red-500 border-2 border-white rounded-full" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{u.first_name} {u.last_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Mail className="w-3.5 h-3.5 text-gray-400" />
                              <p className="text-sm text-gray-600">{u.email}</p>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              {getRoleBadge(u.role)}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleUnlockUser(u.id)}
                          className="w-full sm:w-auto gap-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-200 transition-all font-medium"
                          size="sm"
                        >
                          <Unlock className="h-4 w-4" />
                          Débloquer l'accès
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Templates Tab */}

          {/* Contact Admin Tab */}
          <TabsContent value="contacts" className="space-y-6">
            <ContactAdminTab />
          </TabsContent>

          {/* Permissions Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="permissions" className="space-y-6">
              <PermissionsTab />
            </TabsContent>
          )}

          {/* Labs Admin Tab */}
          {isAdmin && (
            <TabsContent value="labs" className="space-y-6">
              <LabsAdminTab />
            </TabsContent>
          )}

          <TabsContent value="email-templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Templates d'emails
                </CardTitle>
                <CardDescription>
                  Prévisualisez les modèles d'emails envoyés par la plateforme
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : emailTemplates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Cliquez sur l'onglet pour charger les templates</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Template list */}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {emailTemplates.map((tpl) => (
                        <div
                          key={tpl.key}
                          className={`p-4 border rounded-xl cursor-pointer transition-all hover:shadow-md hover:border-primary/40 ${
                            previewTemplate?.key === tpl.key 
                              ? 'border-primary bg-primary/5 shadow-md ring-2 ring-primary/20' 
                              : 'border-gray-200 bg-white'
                          }`}
                          onClick={() => setPreviewTemplate(tpl)}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-sm text-gray-900">{tpl.label}</h4>
                              <p className="text-xs text-gray-500 mt-1">{tpl.description}</p>
                            </div>
                            <div className="w-2 h-2 rounded-full bg-green-400 mt-1.5 shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Preview area */}
                    {previewTemplate && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Eye className="h-4 w-4 text-primary" />
                            Prévisualisation : {previewTemplate.label}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreviewTemplate(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            Fermer
                          </Button>
                        </div>
                        <div className="border rounded-xl overflow-hidden bg-gray-50 shadow-inner">
                          <iframe
                            title={`Preview: ${previewTemplate.label}`}
                            srcDoc={previewTemplate.preview_html}
                            className="w-full border-0"
                            style={{ minHeight: '600px' }}
                            sandbox=""
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup & Restore Tab */}
          {(isAdmin || permissions?.can_access_backup) && (
            <TabsContent value="backup" className="space-y-6">
              <BackupAdminTab />
            </TabsContent>
          )}

          {/* Temporary Sessions Tab */}
          {(isAdmin || permissions?.can_access_temp_sessions) && (
            <TabsContent value="temp-sessions" className="space-y-6">
              <TempSessionsAdminTab />
            </TabsContent>
          )}

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Gestion des utilisateurs</CardTitle>
                  <CardDescription>{users.length} utilisateur(s) inscrits</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {selectedUsers.size > 0 && (
                    <Button variant="destructive" size="sm" className="gap-2" onClick={handleDeleteSelected} disabled={deleting}>
                      {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      Supprimer ({selectedUsers.size})
                    </Button>
                  )}
                  {/* Create User Button */}
                  <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
                    <DialogTrigger asChild>
                      <Button className="gap-2" data-testid="create-user-btn">
                        <UserPlus className="h-4 w-4" />
                        Créer un utilisateur
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="first_name">Prénom *</Label>
                            <Input
                              id="first_name"
                              placeholder="Jean"
                              value={newUser.first_name}
                              onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                              required
                              data-testid="new-user-firstname"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="last_name">Nom *</Label>
                            <Input
                              id="last_name"
                              placeholder="Dupont"
                              value={newUser.last_name}
                              onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                              required
                              data-testid="new-user-lastname"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="jean.dupont@email.com"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            required
                            data-testid="new-user-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Mot de passe *</Label>
                          <Input
                            id="password"
                            type="password"
                            placeholder="Minimum 6 caractères"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            required
                            data-testid="new-user-password"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="role">Rôle</Label>
                          <Select 
                            value={newUser.role} 
                            onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                          >
                            <SelectTrigger data-testid="new-user-role">
                              <SelectValue placeholder="Sélectionner un rôle" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="candidate">Candidat (Apprenant)</SelectItem>
                              <SelectItem value="trainer">Formateur</SelectItem>
                              <SelectItem value="manager">Gestionnaire</SelectItem>
                              <SelectItem value="admin">Administrateur</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setCreateUserOpen(false)}>
                            Annuler
                          </Button>
                          <Button type="submit" disabled={creatingUser} data-testid="submit-create-user">
                            {creatingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Créer l'utilisateur
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const ROLE_CONFIG = {
                    admin: { label: "Administrateurs", color: "#6366f1", bg: "rgba(99,102,241,0.08)", icon: "🛡️" },
                    manager: { label: "Gestionnaires", color: "#8B5CF6", bg: "rgba(139,92,246,0.08)", icon: "⚙️" },
                    trainer: { label: "Formateurs", color: "#10B981", bg: "rgba(16,185,129,0.08)", icon: "🎓" },
                    candidate: { label: "Candidats", color: "#F59E0B", bg: "rgba(245,158,11,0.08)", icon: "👤" },
                    student: { label: "Candidats", color: "#F59E0B", bg: "rgba(245,158,11,0.08)", icon: "👤" },
                  };
                  // Group by role
                  const groups = {};
                  users.forEach(u => {
                    const rk = (u.role === "student") ? "candidate" : (u.role || "candidate");
                    if (!groups[rk]) groups[rk] = [];
                    groups[rk].push(u);
                  });
                  // For candidates, sub-group by workspace
                  const wsMap = {};
                  workspaces.forEach(ws => { wsMap[ws.id] = ws.name || ws.id; });
                  const candidatesByWs = {};
                  (groups["candidate"] || []).forEach(u => {
                    const wsIds = (workspaces.filter(ws => (ws.participants || []).includes(u.id))).map(ws => ws.id);
                    if (wsIds.length === 0) {
                      if (!candidatesByWs["__none__"]) candidatesByWs["__none__"] = [];
                      candidatesByWs["__none__"].push(u);
                    } else {
                      wsIds.forEach(wsId => {
                        if (!candidatesByWs[wsId]) candidatesByWs[wsId] = [];
                        candidatesByWs[wsId].push(u);
                      });
                    }
                  });
                  const roleOrder = ["admin", "manager", "trainer", "candidate"];
                  const renderUserRow = (u, index) => {
                    const isMe = u.id === user?.id;
                    return (
                      <tr key={u.id} data-testid={`user-row-${index}`} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "10px 14px", width: 40 }}>
                          {!isMe && (
                            <input type="checkbox" checked={selectedUsers.has(u.id)} onChange={() => toggleSelectUser(u.id)}
                              style={{ accentColor: "#6366f1", width: 16, height: 16, cursor: "pointer" }} />
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {getInitials(u.first_name, u.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{u.first_name} {u.last_name}</span>
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>{u.email}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <Select value={u.role} onValueChange={(value) => handleUpdateRole(u.id, value)} disabled={isMe}>
                            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="trainer">Formateur</SelectItem>
                              <SelectItem value="manager">Gestionnaire</SelectItem>
                              <SelectItem value="candidate">Candidat</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          {u.is_approved === false ? (
                            <Badge variant="outline" className="gap-1 text-orange-600 border-orange-300 bg-orange-50">
                              <Clock className="h-3 w-3" /> En attente
                            </Badge>
                          ) : isAccountExpired(u.account_expires_at) ? (
                            <Badge variant="destructive" className="gap-1">
                              <XCircle className="h-3 w-3" /> Expiré
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-green-600 border-green-300 bg-green-50">
                              <CheckCircle className="h-3 w-3" /> Actif
                            </Badge>
                          )}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => openExpirationDialog(u)} data-testid={`expiration-user-${index}`}>
                            <CalendarClock className="h-3 w-3" />
                            {u.account_expires_at ? formatDate(u.account_expires_at) : "Non définie"}
                          </Button>
                        </td>
                        <td style={{ padding: "10px 14px" }}>{formatDate(u.created_at)}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditUser(u)} data-testid={`edit-user-${index}`}>
                              <Pencil className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            {!isMe && (
                              <Button variant="ghost" size="icon" onClick={() => setDeleteUserId(u.id)} data-testid={`delete-user-${index}`}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  };
                  const TH = { padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted-foreground)" };
                  const renderTable = (userList) => (
                    <div className="rounded-lg border overflow-x-auto" style={{ marginTop: 8 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid var(--border)", background: "var(--muted)" }}>
                            <th style={{ ...TH, width: 40 }}>
                              <input type="checkbox"
                                checked={userList.filter(u => u.id !== user?.id).length > 0 && userList.filter(u => u.id !== user?.id).every(u => selectedUsers.has(u.id))}
                                onChange={() => toggleSelectAll(userList.filter(u => u.id !== user?.id).map(u => u.id))}
                                style={{ accentColor: "#6366f1", width: 16, height: 16, cursor: "pointer" }} />
                            </th>
                            <th style={TH}>Utilisateur</th>
                            <th style={TH}>Email</th>
                            <th style={TH}>Rôle</th>
                            <th style={TH}>Statut</th>
                            <th style={TH}>Expiration</th>
                            <th style={TH}>Inscrit le</th>
                            <th style={TH}></th>
                          </tr>
                        </thead>
                        <tbody>{userList.map((u, i) => renderUserRow(u, i))}</tbody>
                      </table>
                    </div>
                  );
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                      {roleOrder.filter(r => groups[r] && groups[r].length > 0).map(role => {
                        const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.candidate;
                        const roleUsers = groups[role];
                        if (role === "candidate") {
                          return (
                            <div key={role} style={{ borderRadius: 14, border: `1px solid ${cfg.color}30`, overflow: "hidden" }}>
                              <div style={{ background: cfg.bg, padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                  <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                                  <span style={{ fontWeight: 700, fontSize: 15, color: cfg.color }}>{cfg.label}</span>
                                  <Badge style={{ background: `${cfg.color}20`, color: cfg.color, borderRadius: 20, fontSize: 11 }}>{roleUsers.length}</Badge>
                                </div>
                                <Button variant="outline" size="sm" className="gap-1 text-xs" style={{ borderRadius: 20 }}
                                  onClick={() => toggleSelectAll(roleUsers.filter(u => u.id !== user?.id).map(u => u.id))}>
                                  Tout sélectionner
                                </Button>
                              </div>
                              <div style={{ padding: "0 12px 12px" }}>
                                {Object.entries(candidatesByWs).map(([wsId, wsUsers]) => (
                                  <div key={wsId} style={{ marginTop: 12 }}>
                                    <div 
                                      style={{ fontSize: 12, fontWeight: 700, color: "var(--muted-foreground)", padding: "6px 8px", background: "var(--muted)", borderRadius: 8, marginBottom: 4, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                                      onClick={() => setCollapsedCandidateGroups(prev => ({ ...prev, [wsId]: !prev[wsId] }))}
                                      className="hover:bg-muted/80 transition-colors"
                                    >
                                      {collapsedCandidateGroups[wsId] ? <ChevronRight className="h-4 w-4 text-red-500" /> : <ChevronDown className="h-4 w-4 text-red-500" />}
                                      <span style={{ fontSize: 14 }}>📂</span>
                                      {wsId === "__none__" ? "Sans workspace" : (wsMap[wsId] || wsId)}
                                      <Badge variant="outline" style={{ fontSize: 10, marginLeft: 4 }}>{wsUsers.length}</Badge>
                                    </div>
                                    {!collapsedCandidateGroups[wsId] && renderTable(wsUsers)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={role} style={{ borderRadius: 14, border: `1px solid ${cfg.color}30`, overflow: "hidden" }}>
                            <div style={{ background: cfg.bg, padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ fontSize: 18 }}>{cfg.icon}</span>
                                <span style={{ fontWeight: 700, fontSize: 15, color: cfg.color }}>{cfg.label}</span>
                                <Badge style={{ background: `${cfg.color}20`, color: cfg.color, borderRadius: 20, fontSize: 11 }}>{roleUsers.length}</Badge>
                              </div>
                              <Button variant="outline" size="sm" className="gap-1 text-xs" style={{ borderRadius: 20 }}
                                onClick={() => toggleSelectAll(roleUsers.filter(u => u.id !== user?.id).map(u => u.id))}>
                                Tout sélectionner
                              </Button>
                            </div>
                            {renderTable(roleUsers)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Edit User Dialog */}
            <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Modifier l'utilisateur</DialogTitle>
                </DialogHeader>
                {editUser && (
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="edit_first_name">Prénom *</Label>
                        <Input
                          id="edit_first_name"
                          value={editUser.first_name}
                          onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
                          required
                          data-testid="edit-user-firstname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_last_name">Nom *</Label>
                        <Input
                          id="edit_last_name"
                          value={editUser.last_name}
                          onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
                          required
                          data-testid="edit-user-lastname"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_email">Email *</Label>
                      <Input
                        id="edit_email"
                        type="email"
                        value={editUser.email}
                        onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                        required
                        data-testid="edit-user-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_role">Rôle</Label>
                      <Select 
                        value={editUser.role} 
                        onValueChange={(value) => setEditUser({ ...editUser, role: value })}
                        disabled={editUser.id === user?.id}
                      >
                        <SelectTrigger data-testid="edit-user-role">
                          <SelectValue placeholder="Sélectionner un rôle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="candidate">Candidat (Apprenant)</SelectItem>
                          <SelectItem value="trainer">Formateur</SelectItem>
                          <SelectItem value="manager">Gestionnaire</SelectItem>
                          <SelectItem value="admin">Administrateur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_password">Nouveau mot de passe (optionnel)</Label>
                      <Input
                        id="edit_password"
                        type="password"
                        placeholder="Laisser vide pour ne pas modifier"
                        value={editUser.new_password}
                        onChange={(e) => setEditUser({ ...editUser, new_password: e.target.value })}
                        data-testid="edit-user-password"
                      />
                      <p className="text-xs text-muted-foreground">
                        Minimum 6 caractères si vous souhaitez changer le mot de passe
                      </p>
                    </div>
                    
                    {/* Statut du compte */}
                    <div className="space-y-4 pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label>Compte actif</Label>
                          <p className="text-xs text-muted-foreground">
                            Désactiver bloquera la connexion de l'utilisateur
                          </p>
                        </div>
                        <Switch
                          checked={editUser.is_approved !== false}
                          onCheckedChange={(checked) => setEditUser({ ...editUser, is_approved: checked })}
                          disabled={editUser.id === user?.id}
                          data-testid="edit-user-approved"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_expiration">Date d'expiration (optionnel)</Label>
                        <Input
                          id="edit_expiration"
                          type="date"
                          value={editUser.account_expires_at ? editUser.account_expires_at.split('T')[0] : ''}
                          onChange={(e) => setEditUser({ ...editUser, account_expires_at: e.target.value || null })}
                          data-testid="edit-user-expiration"
                        />
                        <p className="text-xs text-muted-foreground">
                          L'utilisateur ne pourra plus se connecter après cette date. Laissez vide pour aucune limite.
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setEditUserOpen(false)}>
                        Annuler
                      </Button>
                      <Button type="submit" disabled={editingUser} data-testid="submit-edit-user">
                        {editingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Enregistrer
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-6">
            {/* Auto Approve Setting - Admin Only */}
            {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Paramètres d'approbation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Approbation automatique</Label>
                    <p className="text-sm text-muted-foreground">
                      Les nouveaux comptes sont automatiquement approuvés sans validation admin
                    </p>
                  </div>
                  <Switch
                    checked={autoApprove}
                    onCheckedChange={handleToggleAutoApprove}
                    data-testid="auto-approve-switch"
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* Pending Users */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  Comptes en attente d'approbation
                </CardTitle>
                <CardDescription>
                  {pendingUsers.length} utilisateur(s) en attente
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p>Aucune demande en attente</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map((u, index) => (
                      <div 
                        key={u.id} 
                        className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 dark:bg-orange-900/10"
                        data-testid={`pending-user-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-orange-200 text-orange-800">
                              {getInitials(u.first_name, u.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(u.role)}
                              <span className="text-xs text-muted-foreground">
                                Inscrit le {formatDate(u.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(u.id)}
                            className="gap-1 bg-green-600 hover:bg-green-700"
                            data-testid={`approve-user-${index}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                            Approuver
                          </Button>
                          {isAdmin && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectUser(u.id)}
                            className="gap-1"
                            data-testid={`reject-user-${index}`}
                          >
                            <XCircle className="h-4 w-4" />
                            Rejeter
                          </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="space-y-6">
            {/* SMTP Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <CardTitle>Configuration SMTP</CardTitle>
                </div>
                <CardDescription>
                  Configurez le serveur email pour les notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Serveur SMTP</Label>
                    <Input
                      placeholder="smtp.example.com"
                      value={smtpConfig.host}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={smtpConfig.port}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) || 587 })}
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Nom d'utilisateur</Label>
                    <Input
                      value={smtpConfig.username}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                      data-testid="smtp-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe</Label>
                    <Input
                      type="password"
                      value={smtpConfig.password}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                      data-testid="smtp-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email d'envoi</Label>
                  <Input
                    type="email"
                    placeholder="noreply@example.com"
                    value={smtpConfig.from_email}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                    data-testid="smtp-from-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={smtpConfig.use_tls}
                    onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked })}
                    data-testid="smtp-tls-switch"
                  />
                  <Label>Utiliser TLS</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => handleSaveConfig("smtp")} 
                    disabled={savingConfig === "smtp"}
                    className="gap-2"
                    data-testid="save-smtp-btn"
                  >
                    {savingConfig === "smtp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Enregistrer
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                    disabled={testingEmail}
                    data-testid="test-smtp-btn"
                    onClick={async () => {
                      const testAddr = window.prompt("Entrez l'adresse e-mail de test :", smtpConfig.username || "");
                      if (!testAddr) return;
                      setTestingEmail(true);
                      try {
                        await adminAPI.testEmail(testAddr);
                        toast.success(`Email de test envoyé avec succès à ${testAddr}`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Échec de l'envoi. Vérifiez la configuration SMTP.");
                      } finally {
                        setTestingEmail(false);
                      }
                    }}
                  >
                    {testingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Tester l'envoi
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Jitsi Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-primary" />
                  <CardTitle>Configuration Jitsi</CardTitle>
                </div>
                <CardDescription>
                  Configurez votre serveur Jitsi pour les visioconférences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>URL du serveur Jitsi</Label>
                  <Input
                    placeholder="https://meet.example.com"
                    value={jitsiConfig.server_url}
                    onChange={(e) => setJitsiConfig({ ...jitsiConfig, server_url: e.target.value })}
                    data-testid="jitsi-url-input"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>App ID (optionnel)</Label>
                    <Input
                      value={jitsiConfig.app_id}
                      onChange={(e) => setJitsiConfig({ ...jitsiConfig, app_id: e.target.value })}
                      data-testid="jitsi-appid-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Clé secrète (optionnel)</Label>
                    <Input
                      type="password"
                      value={jitsiConfig.secret_key}
                      onChange={(e) => setJitsiConfig({ ...jitsiConfig, secret_key: e.target.value })}
                      data-testid="jitsi-secret-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => handleSaveConfig("jitsi")} 
                  disabled={savingConfig === "jitsi"}
                  className="gap-2"
                  data-testid="save-jitsi-btn"
                >
                  {savingConfig === "jitsi" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* S3 Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  <CardTitle>Configuration S3</CardTitle>
                </div>
                <CardDescription>
                  Configurez le stockage S3 compatible pour les fichiers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>URL du endpoint (optionnel pour AWS)</Label>
                  <Input
                    placeholder="https://s3.example.com"
                    value={s3Config.endpoint_url}
                    onChange={(e) => setS3Config({ ...s3Config, endpoint_url: e.target.value })}
                    data-testid="s3-endpoint-input"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Access Key</Label>
                    <Input
                      value={s3Config.access_key}
                      onChange={(e) => setS3Config({ ...s3Config, access_key: e.target.value })}
                      data-testid="s3-accesskey-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secret Key</Label>
                    <Input
                      type="password"
                      value={s3Config.secret_key}
                      onChange={(e) => setS3Config({ ...s3Config, secret_key: e.target.value })}
                      data-testid="s3-secretkey-input"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Nom du bucket</Label>
                    <Input
                      value={s3Config.bucket_name}
                      onChange={(e) => setS3Config({ ...s3Config, bucket_name: e.target.value })}
                      data-testid="s3-bucket-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Région</Label>
                    <Input
                      value={s3Config.region}
                      onChange={(e) => setS3Config({ ...s3Config, region: e.target.value })}
                      data-testid="s3-region-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => handleSaveConfig("s3")} 
                  disabled={savingConfig === "s3"}
                  className="gap-2"
                  data-testid="save-s3-btn"
                >
                  {savingConfig === "s3" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* Google Drive Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-primary" />
                  <CardTitle>Configuration Google Drive</CardTitle>
                </div>
                <CardDescription>
                  Connectez Google Drive pour le stockage automatique des documents candidats
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Credentials JSON (Service Account)</Label>
                  <div className="relative">
                    <textarea
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[100px] font-mono text-xs"
                      placeholder='{"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}'
                      value={showGdriveKey ? gdriveConfig.credentials_json : (gdriveConfig.credentials_json ? "••••••••••••••••" : "")}
                      onChange={(e) => setGdriveConfig({ ...gdriveConfig, credentials_json: e.target.value })}
                      onFocus={() => setShowGdriveKey(true)}
                      data-testid="gdrive-credentials-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setShowGdriveKey(!showGdriveKey)}
                    >
                      {showGdriveKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Collez le JSON complet du compte de service Google. Le dossier racine doit être partagé avec l'email du service account.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>ID du dossier racine Google Drive</Label>
                  <Input
                    placeholder="1ABCdef...xyz"
                    value={gdriveConfig.root_folder_id}
                    onChange={(e) => setGdriveConfig({ ...gdriveConfig, root_folder_id: e.target.value })}
                    data-testid="gdrive-folder-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    L'ID se trouve dans l'URL du dossier Drive : drive.google.com/drive/folders/<strong>ID_ICI</strong>
                  </p>
                </div>
                {gdriveConfig.credentials_json && gdriveConfig.root_folder_id && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm text-emerald-600 dark:text-emerald-400">Google Drive configuré</span>
                  </div>
                )}
                {!gdriveConfig.credentials_json && (
                  <div className="p-3 bg-muted/50 border rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Non configuré — les documents seront stockés localement uniquement</span>
                  </div>
                )}
                <Button 
                  onClick={() => handleSaveConfig("gdrive")} 
                  disabled={savingConfig === "gdrive"}
                  className="gap-2"
                  data-testid="save-gdrive-btn"
                >
                  {savingConfig === "gdrive" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* Sharing Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-primary" />
                  <CardTitle>Configuration du Partage</CardTitle>
                </div>
                <CardDescription>
                  Configurez le domaine personnalisé pour les liens de partage public
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!sharingConfig.sharing_enabled && (
                  <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                    <p className="text-destructive text-sm">
                      Le partage est désactivé pour votre organisation. Contactez l'administrateur de la plateforme pour l'activer.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Domaine de partage personnalisé</Label>
                  <Input
                    placeholder="partage.votre-domaine.com"
                    value={sharingConfig.share_domain || ""}
                    onChange={(e) => setSharingConfig({ ...sharingConfig, share_domain: e.target.value })}
                    disabled={!sharingConfig.sharing_enabled}
                    data-testid="share-domain-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Configurez un sous-domaine dédié pour vos liens de partage (ex: partage.votre-entreprise.com). 
                    Assurez-vous que ce domaine pointe vers votre serveur.
                  </p>
                </div>
                <Button 
                  onClick={async () => {
                    setSavingConfig("sharing");
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sharing/config?share_domain=${encodeURIComponent(sharingConfig.share_domain || '')}`, {
                        method: 'PUT',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                      });
                      if (res.ok) {
                        toast.success("Configuration de partage enregistrée");
                      } else {
                        const error = await res.json();
                        toast.error(error.detail || "Erreur lors de l'enregistrement");
                      }
                    } catch (e) {
                      toast.error("Erreur de connexion");
                    } finally {
                      setSavingConfig(null);
                    }
                  }} 
                  disabled={savingConfig === "sharing" || !sharingConfig.sharing_enabled}
                  className="gap-2"
                  data-testid="save-sharing-btn"
                >
                  {savingConfig === "sharing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* Allowed Email Domains Config */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  <CardTitle>Domaines Email Autorisés</CardTitle>
                </div>
                <CardDescription>
                  Gérez les domaines email autorisés pour l'inscription. Les modifications sont appliquées immédiatement sans redémarrage.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Source indicator */}
                <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
                  emailDomainsSource === "database" 
                    ? "bg-blue-500/10 text-blue-600 border border-blue-200" 
                    : "bg-amber-500/10 text-amber-600 border border-amber-200"
                }`}>
                  <AlertCircle className="h-3.5 w-3.5" />
                  {emailDomainsSource === "database" 
                    ? "Configuration personnalisée (base de données)" 
                    : "Configuration par défaut (.env)"}
                </div>

                {loadingDomains ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Domain chips */}
                    <div className="flex flex-wrap gap-2">
                      {emailDomains.map((domain) => (
                        <span
                          key={domain}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20 transition-all hover:bg-primary/15"
                        >
                          <Globe className="h-3.5 w-3.5" />
                          {domain}
                          <button
                            onClick={() => handleRemoveDomain(domain)}
                            className="ml-0.5 p-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
                            title={`Supprimer ${domain}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      {emailDomains.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">Aucun domaine configuré</p>
                      )}
                    </div>

                    {/* Add domain input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="ex: mon-entreprise.com"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain(); } }}
                        data-testid="email-domain-input"
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        onClick={handleAddDomain}
                        className="gap-1.5"
                        data-testid="add-domain-btn"
                      >
                        <Plus className="h-4 w-4" />
                        Ajouter
                      </Button>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-3 pt-2">
                      <Button 
                        onClick={handleSaveEmailDomains} 
                        disabled={savingDomains}
                        className="gap-2"
                        data-testid="save-domains-btn"
                      >
                        {savingDomains ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer
                      </Button>
                      {emailDomainsSource === "database" && (
                        <Button
                          variant="outline"
                          onClick={handleResetEmailDomains}
                          disabled={savingDomains}
                          className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                          data-testid="reset-domains-btn"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Réinitialiser (.env)
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Mon Dossier Visibility */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <CardTitle>Menu "Mon dossier"</CardTitle>
                </div>
                <CardDescription>
                  Activez ou désactivez le menu "Mon dossier" pour les candidats dans la barre latérale
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Afficher "Mon dossier"</Label>
                    <p className="text-sm text-muted-foreground">
                      {monDossierEnabled
                        ? "Le menu est visible pour tous les candidats"
                        : "Le menu est masqué pour les candidats"}
                    </p>
                  </div>
                  <Switch
                    checked={monDossierEnabled}
                    onCheckedChange={handleToggleMonDossier}
                    data-testid="mon-dossier-switch"
                  />
                </div>
              </CardContent>
            </Card>

          </TabsContent>

          {/* LLM Config Tab */}
          <TabsContent value="llm-config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  Configuration IA
                </CardTitle>
                <CardDescription>Configurez le modèle de langage utilisé pour la génération de contenu</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Workspace selector */}
                <div className="space-y-2">
                  <Label>Workspace</Label>
                  <select
                    value={llmWorkspaceId}
                    onChange={(e) => handleSelectLlmWorkspace(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Sélectionnez un workspace</option>
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                    ))}
                  </select>
                </div>

                {loadingLlm ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : !llmWorkspaceId ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Sélectionnez un workspace pour configurer l'IA
                  </div>
                ) : (
                  <>
                    {/* Provider */}
                    <div className="space-y-2">
                      <Label>Fournisseur</Label>
                      <Select
                        value={llmConfig.provider}
                        onValueChange={(value) => {
                          const provider = llmProviders.find(p => p.value === value);
                          setLlmConfig(prev => ({ ...prev, provider: value, model: provider?.models[0]?.value || '' }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {llmProviders.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Model */}
                    <div className="space-y-2">
                      <Label>Modèle</Label>
                      <Select
                        value={llmConfig.model}
                        onValueChange={(value) => setLlmConfig(prev => ({ ...prev, model: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getLlmModels().map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* API Key */}
                    <div className="space-y-4">

                      {true && (
                        <div className="space-y-2">
                          <Label>Clé API personnalisée</Label>
                          <div className="relative">
                            <Input
                              type={showApiKey ? 'text' : 'password'}
                              value={llmConfig.api_key}
                              onChange={(e) => setLlmConfig(prev => ({ ...prev, api_key: e.target.value }))}
                              placeholder={hasCustomKey ? '••••••••••••••••' : 'sk-...'}
                            />
                            <Button type="button" variant="ghost" size="sm" className="absolute right-2 top-1/2 -translate-y-1/2" onClick={() => setShowApiKey(!showApiKey)}>
                              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          </div>
                          {hasCustomKey && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              Une clé personnalisée est configurée
                            </p>
                          )}
                        </div>
                      )}

                      
                    </div>

                    {/* Save */}
                    <div className="flex justify-end">
                      <Button onClick={handleSaveLlmConfig} disabled={savingLlm}>
                        {savingLlm ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Enregistrer
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Git Connections Tab */}
          <TabsContent value="git-connections" className="space-y-6">
            <GitConnectionsConfig userId={user?.id} />
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle>Gestion des groupes</CardTitle>
                  <CardDescription>Organisez les utilisateurs par groupe</CardDescription>
                </div>
                <Button className="gap-2" onClick={() => openAdminGroupDialog()} data-testid="admin-create-group-btn">
                  <Plus className="h-4 w-4" />
                  Nouveau groupe
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loadingGroups ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : adminGroups.length === 0 ? (
                    <div className="flex flex-col items-center py-8 text-muted-foreground">
                      <UsersRound className="h-10 w-10 mb-3 opacity-50" />
                      <p>Aucun groupe</p>
                      <Button className="mt-3 gap-2" variant="outline" onClick={() => openAdminGroupDialog()}>
                        <Plus className="h-4 w-4" />
                        Créer un groupe
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {adminGroups.map((group) => {
                        const members = adminGroupMembers[group.id] || [];
                        const isExpanded = expandedAdminGroups[group.id];
                        return (
                          <div key={group.id} className="border rounded-lg overflow-hidden">
                            <div
                              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                              onClick={() => setExpandedAdminGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              <UsersRound className="h-5 w-5 text-primary" />
                              <div className="flex-1">
                                <span className="font-medium">{group.name}</span>
                                {group.description && <span className="text-sm text-muted-foreground ml-2">- {group.description}</span>}
                              </div>
                              <Badge variant="secondary">{members.length} membre(s)</Badge>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedGroupForMember(group.id); setMemberSearchTerm(""); setAddMemberDialogOpen(true); }}>
                                  <UserPlus className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAdminGroupDialog(group)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteGroupId(group.id)}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="border-t divide-y">
                                {members.length === 0 ? (
                                  <p className="text-sm text-muted-foreground text-center py-3">Aucun membre</p>
                                ) : members.map(m => (
                                  <div key={m.id} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/20">
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                        {getInitials(m.first_name, m.last_name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium">{m.first_name} {m.last_name}</p>
                                      <p className="text-xs text-muted-foreground">{m.email}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveAdminMember(group.id, m.id)}>
                                      <UserMinus className="h-3 w-3 text-destructive" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Group Create/Edit Dialog */}
            <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingGroup ? "Modifier le groupe" : "Nouveau groupe"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nom du groupe *</Label>
                    <Input
                      placeholder="Ex: Groupe A"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                      data-testid="admin-group-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      placeholder="Description optionnelle"
                      value={groupForm.description}
                      onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setGroupDialogOpen(false)}>Annuler</Button>
                    <Button className="flex-1" onClick={handleSaveAdminGroup} disabled={savingGroup || !groupForm.name.trim()}>
                      {savingGroup && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      {editingGroup ? "Modifier" : "Créer"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Member Dialog */}
            <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Ajouter un membre</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher..."
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {(() => {
                      const group = adminGroups.find(g => g.id === selectedGroupForMember);
                      const memberIds = group?.members || [];
                      const available = users.filter(p =>
                        !memberIds.includes(p.id) &&
                        (`${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(memberSearchTerm.toLowerCase()))
                      );
                      return available.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">Aucun participant disponible</p>
                      ) : available.map(u => (
                        <div key={u.id} className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 transition-colors">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {getInitials(u.first_name, u.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                          <Button size="sm" onClick={() => handleAddAdminMember(u.id)} disabled={addingMember === u.id}>
                            {addingMember === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                          </Button>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete Group Confirmation */}
            <AlertDialog open={!!deleteGroupId && activeTab === 'groups'} onOpenChange={() => setDeleteGroupId(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce groupe ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Les membres ne seront pas supprimés de l&apos;espace de travail.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAdminGroup} disabled={deletingGroup}>
                    {deletingGroup && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>

          {/* Documentation Tab */}
          {isAdmin && (
            <TabsContent value="documentation" className="space-y-6">
              <DocumentationAdminTab />
            </TabsContent>
          )}

          {/* Security Tab */}
          {isAdmin && (
            <TabsContent value="security" className="space-y-6">
              <SecurityAdminTab />
            </TabsContent>
          )}

          {/* Tutorials Admin Tab */}
          {isAdmin && (
            <TabsContent value="tutorials">
              <TutorialsListPage />
            </TabsContent>
          )}
        </Tabs>

        {/* Delete User Dialog */}
        <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. L'utilisateur sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Expiration Date Dialog */}
        <Dialog open={expirationDialogOpen} onOpenChange={setExpirationDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5" />
                Date de validité du compte
              </DialogTitle>
            </DialogHeader>
            {expirationUser && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{expirationUser.first_name} {expirationUser.last_name}</p>
                  <p className="text-sm text-muted-foreground">{expirationUser.email}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiration_date">Date d'expiration</Label>
                  <Input
                    id="expiration_date"
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    data-testid="expiration-date-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    L'utilisateur ne pourra plus se connecter après cette date. Laissez vide pour supprimer la limite.
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setExpirationDialogOpen(false);
                      setExpirationUser(null);
                    }}
                  >
                    Annuler
                  </Button>
                  {expirationDate && (
                    <Button 
                      type="button" 
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => setExpirationDate("")}
                    >
                      Supprimer la limite
                    </Button>
                  )}
                  <Button 
                    onClick={handleSetExpiration} 
                    disabled={savingExpiration}
                    data-testid="save-expiration-btn"
                  >
                    {savingExpiration && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

// Permissions Tab Component (Admin Only)
function PermissionsTab() {
  const [permUsers, setPermUsers] = useState([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [updatingPerms, setUpdatingPerms] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUsers, setExpandedUsers] = useState({});

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoadingPerms(true);
    try {
      const res = await adminAPI.listPermissions();
      setPermUsers(res.data);
      // Expand first user by default
      if (res.data.length > 0) {
        setExpandedUsers({ [res.data[0].user_id]: true });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des permissions");
    } finally {
      setLoadingPerms(false);
    }
  };

  const togglePermission = async (userId, field, currentValue) => {
    setUpdatingPerms(prev => ({ ...prev, [`${userId}-${field}`]: true }));
    try {
      await adminAPI.updatePermissions(userId, { [field]: !currentValue });
      setPermUsers(prev => prev.map(u => 
        u.user_id === userId 
          ? { ...u, permissions: { ...u.permissions, [field]: !currentValue } }
          : u
      ));
      toast.success("Permission mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdatingPerms(prev => ({ ...prev, [`${userId}-${field}`]: false }));
    }
  };

  const bulkToggle = async (userId, enable) => {
    const user = permUsers.find(u => u.user_id === userId);
    if (!user) return;
    
    const allFields = Object.keys(permissionCategories).flatMap(cat => 
      permissionCategories[cat].map(p => p.field)
    );
    
    const updates = {};
    allFields.forEach(f => { updates[f] = enable; });
    
    // Mark all as updating
    const updatingKeys = {};
    allFields.forEach(f => { updatingKeys[`${userId}-${f}`] = true; });
    setUpdatingPerms(prev => ({ ...prev, ...updatingKeys }));
    
    try {
      await adminAPI.updatePermissions(userId, updates);
      setPermUsers(prev => prev.map(u => 
        u.user_id === userId 
          ? { ...u, permissions: { ...u.permissions, ...updates } }
          : u
      ));
      toast.success(enable ? "Toutes les permissions activées" : "Toutes les permissions désactivées");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      const clearKeys = {};
      allFields.forEach(f => { clearKeys[`${userId}-${f}`] = false; });
      setUpdatingPerms(prev => ({ ...prev, ...clearKeys }));
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const roleLabels = {
    trainer: "Formateur",
    manager: "Gestionnaire",
  };

  const roleBadgeColors = {
    trainer: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    manager: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  };

  const permissionCategories = {
    "Général": [
      { field: "can_create_workspace", label: "Créer des workspaces", icon: "🏢" },
    ],
    "Contenu pédagogique": [
      { field: "can_access_courses", label: "Cours", icon: "📖" },
      { field: "can_access_tp", label: "Travaux Pratiques", icon: "🧪" },
      { field: "can_access_quiz", label: "Quiz", icon: "📋" },
      { field: "can_access_assignments", label: "Devoirs", icon: "📝" },
      { field: "can_access_exams", label: "Examens", icon: "🎓" },
    ],
    "Collaboration": [
      { field: "can_access_draw", label: "Draw (Tableau blanc)", icon: "🎨" },
      { field: "can_access_codeshare", label: "CodeShare", icon: "💻" },
      { field: "can_access_meetings", label: "Réunions vidéo", icon: "📹" },
      { field: "can_access_chat", label: "Chat", icon: "💬" },
    ],
    "Ressources": [
      { field: "can_access_files", label: "Upload de fichiers", icon: "📁" },
      { field: "can_access_videos", label: "Capture vidéo", icon: "🎬" },
    ],
    "Gestion & Suivi": [
      { field: "can_access_planning", label: "Planning", icon: "📅" },
      { field: "can_access_attendance", label: "Présence", icon: "✅" },
      { field: "can_access_absence_requests", label: "Demandes d'absence", icon: "📄" },
      { field: "can_access_progress", label: "Progression", icon: "📊" },
      { field: "can_access_sites", label: "Site Deployer", icon: "🌐" },
      { field: "can_access_participants", label: "Gestion participants", icon: "👥" },
      { field: "can_access_labs", label: "Labs Interactifs", icon: "🧪" },
      { field: "can_access_vms", label: "VMs", icon: "🖥️" },
    ],
    "Administration": [
      { field: "can_access_backup", label: "Backup & Restore", icon: "💾" },
      { field: "can_access_temp_sessions", label: "Sessions Temp.", icon: "📆" },
    ],
  };

  const filteredUsers = permUsers.filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.first_name.toLowerCase().includes(term) ||
      u.last_name.toLowerCase().includes(term) ||
      u.email.toLowerCase().includes(term)
    );
  });

  const getActiveCount = (permissions) => {
    const allFields = Object.keys(permissionCategories).flatMap(cat => 
      permissionCategories[cat].map(p => p.field)
    );
    return allFields.filter(f => permissions?.[f]).length;
  };

  const getTotalCount = () => {
    return Object.keys(permissionCategories).flatMap(cat => 
      permissionCategories[cat].map(p => p.field)
    ).length;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          Gestion des permissions
        </CardTitle>
        <CardDescription>
          Gérez les permissions des menus workspace pour chaque formateur et gestionnaire.
          Activez ou désactivez l'accès aux fonctionnalités par utilisateur.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {loadingPerms ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Users className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">
              {searchTerm ? "Aucun utilisateur trouvé" : "Aucun formateur ou gestionnaire trouvé"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map(u => {
              const isExpanded = expandedUsers[u.user_id];
              const activeCount = getActiveCount(u.permissions);
              const totalCount = getTotalCount();

              return (
                <div
                  key={u.user_id}
                  className={`rounded-xl border transition-all duration-300 ${
                    isExpanded 
                      ? 'ring-2 ring-primary/20 shadow-lg shadow-primary/5' 
                      : 'hover:border-primary/30 hover:shadow-md'
                  }`}
                >
                  {/* User Header */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer select-none"
                    onClick={() => toggleExpand(u.user_id)}
                  >
                    <Avatar className="h-10 w-10 ring-2 ring-primary/10">
                      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-purple-600 text-white text-sm font-bold">
                        {getInitials(u.first_name, u.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{u.first_name} {u.last_name}</p>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${roleBadgeColors[u.role] || ''}`}>
                          {roleLabels[u.role] || u.role}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs font-medium text-muted-foreground">
                          {activeCount}/{totalCount} actives
                        </p>
                        <div className="w-24 h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
                            style={{ width: `${(activeCount / totalCount) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Permissions */}
                  {isExpanded && (
                    <div className="border-t px-4 pb-4 animate-in slide-in-from-top-2 duration-200">
                      {/* Bulk Actions */}
                      <div className="flex items-center justify-end gap-2 py-3 border-b mb-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
                          onClick={(e) => { e.stopPropagation(); bulkToggle(u.user_id, true); }}
                        >
                          <Unlock className="h-3 w-3" />
                          Tout activer
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 dark:text-rose-400 dark:border-rose-800 dark:hover:bg-rose-900/20"
                          onClick={(e) => { e.stopPropagation(); bulkToggle(u.user_id, false); }}
                        >
                          <Lock className="h-3 w-3" />
                          Tout désactiver
                        </Button>
                      </div>

                      {/* Permission Categories */}
                      <div className="space-y-5">
                        {Object.entries(permissionCategories).map(([category, permissions]) => (
                          <div key={category}>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                              <div className="h-px flex-1 bg-border" />
                              {category}
                              <div className="h-px flex-1 bg-border" />
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {permissions.map(perm => {
                                const isActive = u.permissions?.[perm.field] || false;
                                const isUpdating = updatingPerms[`${u.user_id}-${perm.field}`];
                                return (
                                  <div
                                    key={perm.field}
                                    className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-all duration-200 border ${
                                      isActive 
                                        ? 'bg-emerald-50/50 border-emerald-200/50 dark:bg-emerald-900/10 dark:border-emerald-800/30' 
                                        : 'bg-muted/30 border-transparent'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-base flex-shrink-0">{perm.icon}</span>
                                      <span className="text-xs font-medium truncate">{perm.label}</span>
                                    </div>
                                    <Switch
                                      checked={isActive}
                                      onCheckedChange={() => togglePermission(u.user_id, perm.field, isActive)}
                                      disabled={isUpdating}
                                      className="flex-shrink-0"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Contact Admin Tab Component
function ContactAdminTab() {
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ status: "", severity: "" });

  useEffect(() => {
    loadContacts();
    loadStaff();
  }, []);

  const loadContacts = async (filterOverride) => {
    setLoadingContacts(true);
    try {
      const f = filterOverride || filters;
      const params = {};
      if (f.status) params.status = f.status;
      if (f.severity) params.severity = f.severity;
      const res = await contactAPI.listAll(params);
      setContacts(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des demandes");
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadStaff = async () => {
    try {
      const res = await adminAPI.listUsers();
      setStaff(res.data.filter(u => ['admin', 'trainer', 'manager'].includes(u.role)));
    } catch (error) {
      console.error("Failed to load staff:", error);
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await contactAPI.updateStatus(contactId, newStatus);
      setContacts(prev => prev.map(c =>
        c.id === contactId ? { ...c, status: newStatus } : c
      ));
      toast.success("Statut mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAssign = async (contactId, userId) => {
    try {
      await contactAPI.assign(contactId, userId);
      const assignee = staff.find(u => u.id === userId);
      setContacts(prev => prev.map(c =>
        c.id === contactId ? {
          ...c,
          assigned_to: userId,
          assigned_name: assignee ? `${assignee.first_name} ${assignee.last_name}` : '',
          status: 'in_progress'
        } : c
      ));
      toast.success("Demande assignée");
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value === "all" ? "" : value };
    setFilters(newFilters);
    loadContacts(newFilters);
  };

  const severityConfig = {
    low: { label: "Faible", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    medium: { label: "Moyenne", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
    high: { label: "Élevée", color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" },
    critical: { label: "Critique", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
  };

  const statusConfig = {
    open: { label: "Ouverte", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    in_progress: { label: "En cours", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
    resolved: { label: "Résolue", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    closed: { label: "Fermée", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400" },
  };

  const roleLabels = { admin: "Admin", trainer: "Formateur", manager: "Gestionnaire" };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          Demandes de contact
          {contacts.length > 0 && (
            <Badge variant="secondary">{contacts.length}</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Consultez, filtrez et traitez les demandes de contact des étudiants
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange("status", v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="open">Ouverte</SelectItem>
              <SelectItem value="in_progress">En cours</SelectItem>
              <SelectItem value="resolved">Résolue</SelectItem>
              <SelectItem value="closed">Fermée</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.severity || "all"} onValueChange={(v) => handleFilterChange("severity", v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sévérité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="low">Faible</SelectItem>
              <SelectItem value="medium">Moyenne</SelectItem>
              <SelectItem value="high">Élevée</SelectItem>
              <SelectItem value="critical">Critique</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => { setFilters({ status: "", severity: "" }); loadContacts({ status: "", severity: "" }); }}>
            Réinitialiser
          </Button>
        </div>

        {loadingContacts ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">Aucune demande trouvée</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map(c => {
              const sevCfg = severityConfig[c.severity] || {};
              const statCfg = statusConfig[c.status] || {};
              const isExpanded = selectedId === c.id;

              return (
                <div
                  key={c.id}
                  className={`rounded-lg border p-4 transition-all ${isExpanded ? 'ring-2 ring-primary/20 bg-muted/30' : 'hover:bg-muted/30'}`}
                >
                  <div
                    className="flex items-start justify-between gap-3 cursor-pointer"
                    onClick={() => setSelectedId(isExpanded ? null : c.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{c.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${sevCfg.color}`}>
                          {sevCfg.label}
                        </span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statCfg.color}`}>
                          {statCfg.label}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground">{c.user_name}</p>
                      <p className="text-xs text-muted-foreground">{formatRelativeTime(c.created_at)}</p>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs">Étudiant</p>
                          <p className="font-medium">{c.user_name}</p>
                          <p className="text-xs text-muted-foreground">{c.user_email}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs">Groupe</p>
                          <p className="font-medium">{c.group_name || "—"}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Message</p>
                        <div className="rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap">
                          {c.message}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 items-center">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Statut :</Label>
                          <Select value={c.status} onValueChange={(v) => handleStatusChange(c.id, v)}>
                            <SelectTrigger className="h-8 w-32 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Ouverte</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="resolved">Résolue</SelectItem>
                              <SelectItem value="closed">Fermée</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center gap-2">
                          <Label className="text-xs">Assigner à :</Label>
                          <Select value={c.assigned_to || ""} onValueChange={(v) => handleAssign(c.id, v)}>
                            <SelectTrigger className="h-8 w-48 text-xs">
                              <SelectValue placeholder="Sélectionner..." />
                            </SelectTrigger>
                            <SelectContent>
                              {staff.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.first_name} {s.last_name} ({roleLabels[s.role] || s.role})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {c.assigned_name && (
                          <p className="text-xs text-muted-foreground ml-auto">
                            Assigné à : <span className="font-medium">{c.assigned_name}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
